import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, TrendingUp, Clock, Users, Trophy, ArrowLeft, Calendar, Globe, Lock, Smartphone } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export default function ReportingPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [stats, setStats] = useState(null);
  const [adminAnalytics, setAdminAnalytics] = useState(null);
  const [adminError, setAdminError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStatistics();
  }, []);

  useEffect(() => {
    // Try the admin endpoint whenever a session exists.
    // If user isn't an admin the backend returns 403 and we silently hide the section.
    if (session?.access_token) {
      fetchAdminAnalytics();
    }
  }, [session?.access_token]);

  const fetchStatistics = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/analytics/game-statistics`);
      
      if (!response.ok) throw new Error('Failed to fetch statistics');
      
      const data = await response.json();
      setStats(data);
      setLoading(false);
    } catch (err) {
      console.error('Analytics error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const fetchAdminAnalytics = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/admin/analytics/games`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      if (response.status === 403) {
        setAdminError('not_admin');
        return;
      }
      if (!response.ok) throw new Error('Failed to load admin analytics');
      const data = await response.json();
      setAdminAnalytics(data);
    } catch (err) {
      console.error('Admin analytics error:', err);
      setAdminError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-white text-2xl">📊 Loading Analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="bg-red-900/50 border border-red-600 rounded-lg p-6 text-white">
          <h2 className="text-xl font-bold mb-2">❌ Error Loading Analytics</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Game
        </button>
        
        <div className="flex items-center gap-4 mb-2">
          <BarChart3 className="w-10 h-10 text-blue-400" />
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Game Analytics Dashboard
          </h1>
        </div>
        <p className="text-gray-400">Global Thermo Nuclear War ]|[ Statistics</p>
      </div>

      {/* Stats Grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Games */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <Trophy className="w-8 h-8 text-blue-200" />
            <div className="text-3xl font-bold">{stats.total_games}</div>
          </div>
          <div className="text-blue-100 font-medium">Total Games Played</div>
        </div>

        {/* Total Turns */}
        <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-8 h-8 text-purple-200" />
            <div className="text-3xl font-bold">{stats.total_turns_played}</div>
          </div>
          <div className="text-purple-100 font-medium">Total Turns Played</div>
        </div>

        {/* Average Turns */}
        <div className="bg-gradient-to-br from-green-600 to-green-800 rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <Clock className="w-8 h-8 text-green-200" />
            <div className="text-3xl font-bold">{stats.average_turns}</div>
          </div>
          <div className="text-green-100 font-medium">Average Turns/Game</div>
        </div>

        {/* Avg Duration */}
        {stats.average_duration_hours && (
          <div className="bg-gradient-to-br from-orange-600 to-orange-800 rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-8 h-8 text-orange-200" />
              <div className="text-3xl font-bold">{stats.average_duration_hours}h</div>
            </div>
            <div className="text-orange-100 font-medium">Average Duration</div>
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Longest Games */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Trophy className="w-6 h-6 text-yellow-400" />
            Longest Games
          </h2>
          
          {stats.longest_by_turns && (
            <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
              <div className="text-sm text-gray-400 mb-1">🏆 Most Turns</div>
              <div className="text-xl font-bold text-yellow-400">{stats.longest_by_turns.turns} Turns</div>
              <div className="text-sm text-gray-300">{stats.longest_by_turns.game_name}</div>
            </div>
          )}

          {stats.longest_by_duration && (
            <div className="bg-slate-900/50 rounded-lg p-4">
              <div className="text-sm text-gray-400 mb-1">⏱️ Longest Duration</div>
              <div className="text-xl font-bold text-blue-400">{stats.longest_by_duration.hours} Hours</div>
              <div className="text-sm text-gray-300">{stats.longest_by_duration.game_name}</div>
            </div>
          )}

          {!stats.longest_by_turns && !stats.longest_by_duration && (
            <div className="text-gray-400 text-center py-8">
              No game data available yet
            </div>
          )}
        </div>

        {/* Country Selections */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Users className="w-6 h-6 text-green-400" />
            Most Picked Countries
          </h2>
          
          {Object.keys(stats.country_selections).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(stats.country_selections)
                .slice(0, 5)
                .map(([country, count], index) => {
                  const percentage = (count / stats.total_games * 100).toFixed(1);
                  const medals = ['🥇', '🥈', '🥉'];
                  
                  return (
                    <div key={country} className="bg-slate-900/50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{medals[index] || '🎖️'}</span>
                          <span className="font-semibold">{country}</span>
                        </div>
                        <span className="text-blue-400 font-bold">{count} games</span>
                      </div>
                      {/* Progress bar */}
                      <div className="w-full bg-slate-700 rounded-full h-2 overflow-hidden">
                        <div 
                          className="bg-gradient-to-r from-blue-500 to-purple-500 h-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <div className="text-xs text-gray-400 mt-1">{percentage}% of players</div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="text-gray-400 text-center py-8">
              No player country data yet
            </div>
          )}
        </div>
      </div>

      {/* Games by Date & Player Email */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Games by Date */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Clock className="w-6 h-6 text-purple-400" />
            Games by Date
          </h2>
          
          {stats.games_by_date && Object.keys(stats.games_by_date).length > 0 ? (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {Object.entries(stats.games_by_date).map(([date, count]) => (
                <div key={date} className="bg-slate-900/50 rounded-lg p-3 flex items-center justify-between">
                  <span className="text-gray-300 font-medium">📅 {date}</span>
                  <span className="text-purple-400 font-bold">{count} game{count > 1 ? 's' : ''}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-400 text-center py-8">
              No date data available
            </div>
          )}
        </div>

        {/* Games by Player */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Users className="w-6 h-6 text-cyan-400" />
            Most Active Players
          </h2>
          
          {stats.games_by_player && Object.keys(stats.games_by_player).length > 0 ? (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {Object.entries(stats.games_by_player)
                .slice(0, 10)
                .map(([email, count], index) => (
                  <div key={email} className="bg-slate-900/50 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-lg">{index === 0 ? '👑' : '👤'}</span>
                        <span className="text-gray-300 font-medium truncate" title={email}>
                          {email}
                        </span>
                      </div>
                      <span className="text-cyan-400 font-bold ml-2">{count} game{count > 1 ? 's' : ''}</span>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="text-gray-400 text-center py-8">
              No player data available
            </div>
          )}
        </div>
      </div>

      {/* ===== ADMIN-ONLY: Country picks + Session-time analytics ===== */}
      {adminAnalytics && (
        <div className="max-w-7xl mx-auto mt-8">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-yellow-500/30">
            <Lock className="w-5 h-5 text-yellow-400" />
            <h2 className="text-2xl font-bold text-yellow-400">Admin Insights</h2>
            <span className="text-xs uppercase tracking-wider text-yellow-600 bg-yellow-500/10 px-2 py-1 rounded">Restricted</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Country Picks */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6" data-testid="admin-country-picks">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-orange-400" />
                Country Picks
                <span className="text-xs text-gray-400 font-normal">({adminAnalytics.total_games} games)</span>
              </h3>
              <div className="space-y-3">
                {adminAnalytics.country_picks.map((c) => (
                  <div key={c.code}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-200">{c.label}</span>
                      <span className="text-orange-300 font-mono">
                        {c.count} <span className="text-gray-500">({c.pct}%)</span>
                      </span>
                    </div>
                    <div className="w-full h-2 bg-slate-900 rounded overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-orange-500 to-yellow-400"
                        style={{ width: `${c.pct}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Device Mix */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6" data-testid="admin-devices">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-pink-400" />
                Desktop vs Mobile
                <span className="text-xs text-gray-400 font-normal">
                  ({adminAnalytics.devices?.total_visits ?? 0} visits)
                </span>
              </h3>
              {adminAnalytics.devices?.total_visits > 0 ? (
                <>
                  <div className="bg-slate-900/60 rounded p-3 text-center mb-4">
                    <div className="text-xs text-gray-400 uppercase tracking-wider">Mobile + Tablet Share</div>
                    <div className="text-3xl font-bold text-pink-300">{adminAnalytics.devices.mobile_share_pct}%</div>
                  </div>
                  <div className="space-y-3">
                    {adminAnalytics.devices.breakdown.map((d) => (
                      <div key={d.type}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-200">{d.label}</span>
                          <span className="text-pink-300 font-mono">
                            {d.count} <span className="text-gray-500">({d.pct}%)</span>
                          </span>
                        </div>
                        <div className="w-full h-2 bg-slate-900 rounded overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-pink-500 to-purple-400"
                            style={{ width: `${d.pct}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-4 italic">
                    Tracked once per browser session via User-Agent. Started recording from deploy date.
                  </p>
                </>
              ) : (
                <p className="text-sm text-gray-400 italic">
                  No device visits recorded yet. Data will start populating once the new tracking ships to production.
                </p>
              )}
            </div>
          </div>

          <div className="mb-6">
            {/* Session Time */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6" data-testid="admin-session-time">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-400" />
                Time Spent In Game
              </h3>
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="bg-slate-900/60 rounded p-3 text-center">
                  <div className="text-xs text-gray-400 uppercase tracking-wider">Median</div>
                  <div className="text-2xl font-bold text-blue-300">{adminAnalytics.duration.median_minutes}m</div>
                </div>
                <div className="bg-slate-900/60 rounded p-3 text-center">
                  <div className="text-xs text-gray-400 uppercase tracking-wider">Mean</div>
                  <div className="text-2xl font-bold text-blue-300">{adminAnalytics.duration.mean_minutes}m</div>
                </div>
                <div className="bg-slate-900/60 rounded p-3 text-center">
                  <div className="text-xs text-gray-400 uppercase tracking-wider">P90</div>
                  <div className="text-2xl font-bold text-blue-300">{adminAnalytics.duration.p90_minutes}m</div>
                </div>
              </div>
              <div className="space-y-2">
                {adminAnalytics.duration.buckets.map((b) => (
                  <div key={b.label} className="flex items-center gap-3 text-xs">
                    <div className="w-44 text-gray-300 flex-shrink-0">{b.label}</div>
                    <div className="flex-1 h-3 bg-slate-900 rounded overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-cyan-400"
                        style={{ width: `${b.pct}%` }}
                      />
                    </div>
                    <div className="w-20 text-right text-blue-200 font-mono">
                      {b.count} <span className="text-gray-500">({b.pct}%)</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-4 italic">
                Wall-clock time from game start → last activity. Sessions longer than 24h are capped to avoid skew from abandoned-then-resumed games.
              </p>
            </div>
          </div>
        </div>
      )}

      {adminError === 'not_admin' && (
        <div className="max-w-7xl mx-auto mt-8 bg-slate-800/30 border border-slate-700 rounded-xl p-6 text-center">
          <Lock className="w-6 h-6 text-gray-500 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">
            Additional admin analytics are available to authorized accounts.
          </p>
        </div>
      )}
      {/* ===== END ADMIN-ONLY ===== */}

      {/* Footer */}
      <div className="max-w-7xl mx-auto mt-8 text-center text-gray-500 text-sm">
        <p>📊 Analytics update in real-time as players complete games</p>
      </div>
    </div>
  );
}
